import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../api';
import { Truck, DollarSign, Users, Package, TrendingUp, Calendar } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0);
}

function formatNumber(val) {
  return new Intl.NumberFormat('en-PH').format(val || 0);
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [topDrivers, setTopDrivers] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadData();
  }, [days]);

  async function loadData() {
    setLoading(true);
    try {
      const [statsRes, dailyRes, driversRes, customersRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getDailySummary(days),
        dashboardAPI.getTopDrivers(days),
        dashboardAPI.getTopCustomers(days),
      ]);
      setStats(statsRes.data);
      setDailyData(dailyRes.data);
      setTopDrivers(driversRes.data);
      setTopCustomers(customersRes.data);
    } catch (err) {
      console.error('Dashboard load error:', err);
    }
    setLoading(false);
  }

  if (loading) return <div className="loading"><div className="spinner"></div> Loading dashboard...</div>;
  if (!stats) return <div className="loading">Failed to load dashboard. Is the backend running?</div>;

  const pieData = stats.statusBreakdown.map(s => ({
    name: s.delivery_status || 'UNKNOWN',
    value: s.count
  }));

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Total Deliveries</span>
            <div className="stat-icon icon-blue"><Truck size={20} /></div>
          </div>
          <div className="stat-value">{formatNumber(stats.totalDeliveries)}</div>
          <div className="stat-sub">All time records</div>
        </div>
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Total Amount</span>
            <div className="stat-icon icon-green"><DollarSign size={20} /></div>
          </div>
          <div className="stat-value">{formatCurrency(stats.totalAmount)}</div>
          <div className="stat-sub">All time total</div>
        </div>
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Today's Dispatches</span>
            <div className="stat-icon icon-amber"><Calendar size={20} /></div>
          </div>
          <div className="stat-value">{formatNumber(stats.todayDispatches)}</div>
          <div className="stat-sub">{formatCurrency(stats.todayAmount)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">This Month</span>
            <div className="stat-icon icon-purple"><TrendingUp size={20} /></div>
          </div>
          <div className="stat-value">{formatNumber(stats.monthDispatches)}</div>
          <div className="stat-sub">{formatCurrency(stats.monthAmount)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Customers</span>
            <div className="stat-icon icon-blue"><Users size={20} /></div>
          </div>
          <div className="stat-value">{formatNumber(stats.totalCustomers)}</div>
          <div className="stat-sub">Registered accounts</div>
        </div>
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Pull-Outs</span>
            <div className="stat-icon icon-red"><Package size={20} /></div>
          </div>
          <div className="stat-value">{formatNumber(stats.totalPullOuts)}</div>
          <div className="stat-sub">Total records</div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0 }}>Dispatch Trend</h3>
            <select className="filter-select" value={days} onChange={e => setDays(Number(e.target.value))}>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="dispatch_date" tick={{ fontSize: 11 }} tickFormatter={v => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={v => new Date(v).toLocaleDateString()} />
              <Legend />
              <Line type="monotone" dataKey="total_dispatches" stroke="#3b82f6" strokeWidth={2} name="Total" dot={false} />
              <Line type="monotone" dataKey="delivered" stroke="#10b981" strokeWidth={2} name="Delivered" dot={false} />
              <Line type="monotone" dataKey="pending" stroke="#f59e0b" strokeWidth={2} name="Pending" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Status Distribution</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <p style={{ color: '#64748b', textAlign: 'center', paddingTop: 60 }}>No data</p>}
        </div>
      </div>

      <div className="charts-grid" style={{ marginTop: 0 }}>
        <div className="chart-card">
          <h3>Top Drivers ({days} days)</h3>
          {topDrivers.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topDrivers} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="driver" tick={{ fontSize: 11 }} width={100} />
                <Tooltip />
                <Bar dataKey="trip_count" fill="#3b82f6" name="Trips" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p style={{ color: '#64748b', textAlign: 'center', paddingTop: 60 }}>No driver data</p>}
        </div>

        <div className="chart-card">
          <h3>Top Customers ({days} days)</h3>
          <div className="table-scroll" style={{ maxHeight: 300, overflowY: 'auto' }}>
            <table>
              <thead>
                <tr><th>Customer</th><th style={{ textAlign: 'right' }}>Amount</th></tr>
              </thead>
              <tbody>
                {topCustomers.map((c, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 13 }}>{c.customer_name}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(c.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {topCustomers.length === 0 && <p style={{ color: '#64748b', textAlign: 'center', padding: 40 }}>No customer data</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
